import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ZoomIn, ZoomOut, RotateCw, ChevronLeft, ChevronRight, Maximize2, Info, ShoppingCart, Sparkles, MessageSquare } from "lucide-react";

/**
 * ProtectedLightbox — full-screen image viewer with IP protections.
 *
 * Props:
 *   images: [{ url, url_zoom?, type?, label?, fit? }]  — fit: 'contain' (default for drawings) | 'cover'
 *   initialIndex: number
 *   open: boolean
 *   onClose: () => void
 *
 * Protections:
 *   - Right-click disabled on image
 *   - Drag disabled (draggable=false + onDragStart preventDefault)
 *   - user-select: none, -webkit-user-drag: none
 *   - No download attribute
 *   - Loaded image is not directly linked — src is always the signed preview URL
 *
 * Interactions:
 *   - Scroll / +/- buttons / +/- keys to zoom
 *   - Click-drag to pan when zoomed
 *   - Arrow keys to navigate
 *   - Rotate 90° for landscape drawings
 *   - ESC closes
 */
export const ProtectedLightbox = ({
  images,
  initialIndex = 0,
  open,
  onClose,
  purchaseCta,      // { message, onBuyNow, onAddToCart, onRequestCustom, primaryLabel? }
}) => {
  const [index, setIndex] = useState(initialIndex);
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [ctaOpen, setCtaOpen] = useState(true);
  const dragRef = useRef(null);
  const imgWrapRef = useRef(null);

  const current = images && images[index];

  const resetView = useCallback(() => {
    setScale(1);
    setRotation(0);
    setPan({ x: 0, y: 0 });
  }, []);

  useEffect(() => { setIndex(initialIndex); }, [initialIndex, open]);
  useEffect(() => { resetView(); }, [index, resetView]);

  // Prefetch neighbour images once current has loaded — snappy nav
  const [hdLoaded, setHdLoaded] = useState(false);
  const [zoomSrc, setZoomSrc] = useState(null);
  useEffect(() => { setHdLoaded(false); setZoomSrc(null); }, [index]);
  useEffect(() => {
    if (!open || !images?.length) return;
    const current = images[index];
    if (!current) return;
    // Priority 4: open lightbox instantly using the already-loaded hero URL,
    // then silently upgrade to the zoom (2000px) variant once it's ready.
    const hi = current.url_zoom || current.url;
    if (!hi) return;
    const pf = new Image();
    pf.onload = () => setZoomSrc(hi);
    pf.src = hi;
  }, [open, images, index]);
  useEffect(() => {
    if (!open || !images?.length || !hdLoaded) return;
    // Prefetch a ±2 window so rapid arrow-key nav feels instant.
    const offsets = [1, -1, 2, -2];
    offsets.forEach((off) => {
      const im = images[(index + off + images.length * 2) % images.length];
      if (!im) return;
      const src = im.url_zoom || im.url;
      if (!src) return;
      const pf = new Image();
      pf.src = src;
    });
  }, [hdLoaded, open, images, index]);

  const go = useCallback((delta) => {
    if (!images?.length) return;
    setIndex((i) => (i + delta + images.length) % images.length);
  }, [images]);

  const zoomIn = useCallback(() => setScale((s) => Math.min(6, +(s + 0.5).toFixed(2))), []);
  const zoomOut = useCallback(() => setScale((s) => Math.max(1, +(s - 0.5).toFixed(2))), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "+" || e.key === "=") zoomIn();
      else if (e.key === "-" || e.key === "_") zoomOut();
      else if (e.key === "r" || e.key === "R") setRotation((r) => (r + 90) % 360);
      else if (e.key === "0") resetView();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, go, zoomIn, zoomOut, resetView]);

  const onWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) zoomIn();
    else zoomOut();
  };

  const onMouseDown = (e) => {
    if (scale <= 1) return;
    dragRef.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
  };
  const onMouseMove = (e) => {
    if (!dragRef.current) return;
    setPan({ x: e.clientX - dragRef.current.x, y: e.clientY - dragRef.current.y });
  };
  const onMouseUp = () => { dragRef.current = null; };

  const onContextMenu = (e) => { e.preventDefault(); return false; };
  const onDragStart = (e) => { e.preventDefault(); return false; };

  if (!open || !current) return null;

  const fit = current.fit || (current.type && current.type !== "rendering" ? "contain" : "contain");
  // Priority 4: use hero URL immediately (likely cached from the plan page),
  // upgrade to zoom variant transparently once it's preloaded.
  const useSrc = zoomSrc || current.url || current.url_zoom;

  const content = (
    <div
      className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-sm select-none"
      data-testid="protected-lightbox"
      onContextMenu={onContextMenu}
      style={{ userSelect: "none", WebkitUserSelect: "none" }}
    >
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 md:px-6 py-3 bg-gradient-to-b from-black/70 to-transparent text-white z-10">
        <div className="flex flex-col text-xs md:text-sm">
          <span className="font-semibold" data-testid="lightbox-label">{current.label || (current.type || "Preview")}</span>
          <span className="text-white/60 text-[10px] md:text-xs uppercase tracking-wider">
            {(current.type || "").replace(/_/g, " ")} · {index + 1} / {images.length}
          </span>
        </div>
        <div className="flex items-center gap-1 md:gap-2">
          <IconBtn onClick={zoomOut} title="Zoom out (−)" data-testid="lightbox-zoom-out"><ZoomOut size={16} /></IconBtn>
          <span className="text-xs font-mono min-w-10 text-center">{Math.round(scale * 100)}%</span>
          <IconBtn onClick={zoomIn} title="Zoom in (+)" data-testid="lightbox-zoom-in"><ZoomIn size={16} /></IconBtn>
          <IconBtn onClick={() => setRotation((r) => (r + 90) % 360)} title="Rotate (R)" data-testid="lightbox-rotate"><RotateCw size={16} /></IconBtn>
          <IconBtn onClick={resetView} title="Reset (0)" data-testid="lightbox-reset"><Maximize2 size={16} /></IconBtn>
          <IconBtn onClick={onClose} title="Close (Esc)" data-testid="lightbox-close"><X size={18} /></IconBtn>
        </div>
      </div>

      {/* Nav arrows */}
      {images.length > 1 && (
        <>
          <button
            data-testid="lightbox-prev"
            onClick={() => go(-1)}
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 z-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur text-white p-3 transition"
            aria-label="Previous"
          >
            <ChevronLeft size={22} />
          </button>
          <button
            data-testid="lightbox-next"
            onClick={() => go(1)}
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 z-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur text-white p-3 transition"
            aria-label="Next"
          >
            <ChevronRight size={22} />
          </button>
        </>
      )}

      {/* Image area */}
      <div
        ref={imgWrapRef}
        className="absolute inset-0 flex items-center justify-center overflow-hidden cursor-grab active:cursor-grabbing"
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onContextMenu={onContextMenu}
        onDragStart={onDragStart}
      >
        <div className="relative">
          {/* Blur placeholder — the tiny thumbnail loads instantly */}
          {current.url_thumb && !hdLoaded && (
            <img
              src={current.url_thumb}
              alt=""
              draggable={false}
              onDragStart={onDragStart}
              onContextMenu={onContextMenu}
              aria-hidden="true"
              className="absolute inset-0 m-auto pointer-events-none select-none"
              style={{
                maxWidth: "94vw",
                maxHeight: "82vh",
                objectFit: fit,
                filter: "blur(18px)",
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale}) rotate(${rotation}deg)`,
              }}
            />
          )}
          {/* HD image — fades in when loaded */}
          <img
            src={useSrc}
            alt={current.label || "Preview"}
            draggable={false}
            onDragStart={onDragStart}
            onContextMenu={onContextMenu}
            onLoad={() => setHdLoaded(true)}
            data-protected="true"
            data-testid="lightbox-image"
            style={{
              maxWidth: "94vw",
              maxHeight: "82vh",
              objectFit: fit,
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale}) rotate(${rotation}deg)`,
              transition: dragRef.current ? "none" : "transform 0.18s ease, opacity 0.35s ease",
              opacity: hdLoaded ? 1 : 0,
              userSelect: "none",
              WebkitUserDrag: "none",
              pointerEvents: "auto",
            }}
          />
        </div>
      </div>

      {/* Purchase CTA inside lightbox — collapsible, non-intrusive */}
      {purchaseCta && (
        <div
          className="absolute bottom-16 md:bottom-14 right-3 md:right-6 z-10 max-w-sm"
          data-testid="lightbox-purchase-cta"
        >
          {ctaOpen ? (
            <div className="bg-white/95 backdrop-blur border border-white/20 shadow-2xl rounded-lg p-4 text-slate-900">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-start gap-2 flex-1">
                  <Sparkles size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs leading-relaxed" data-testid="lightbox-cta-message">
                    {purchaseCta.message || "Purchase this plan to access high-resolution files and the complete drawing set."}
                  </p>
                </div>
                <button
                  onClick={() => setCtaOpen(false)}
                  data-testid="lightbox-cta-collapse"
                  className="text-slate-400 hover:text-slate-700 -mt-1"
                  aria-label="Collapse"
                >
                  <X size={14} />
                </button>
              </div>
              <div className="flex flex-col gap-1.5">
                {purchaseCta.onBuyNow && (
                  <button
                    onClick={purchaseCta.onBuyNow}
                    data-testid="lightbox-cta-buy-now"
                    className="w-full bg-slate-900 hover:bg-black text-white text-xs font-semibold uppercase tracking-wider py-2.5 flex items-center justify-center gap-2 transition"
                  >
                    <ShoppingCart size={13} /> {purchaseCta.primaryLabel || "Buy Now"}
                  </button>
                )}
                <div className="flex gap-1.5">
                  {purchaseCta.onAddToCart && (
                    <button
                      onClick={purchaseCta.onAddToCart}
                      data-testid="lightbox-cta-add-cart"
                      className="flex-1 border border-slate-300 hover:border-slate-900 hover:bg-slate-50 text-slate-900 text-[11px] font-medium uppercase tracking-wider py-2 transition"
                    >
                      Add to Cart
                    </button>
                  )}
                  {purchaseCta.onRequestCustom && (
                    <button
                      onClick={purchaseCta.onRequestCustom}
                      data-testid="lightbox-cta-request-custom"
                      className="flex-1 border border-slate-300 hover:border-slate-900 hover:bg-slate-50 text-slate-900 text-[11px] font-medium uppercase tracking-wider py-2 flex items-center justify-center gap-1 transition"
                    >
                      <MessageSquare size={11} /> Customize
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setCtaOpen(true)}
              data-testid="lightbox-cta-expand"
              className="bg-white/90 hover:bg-white backdrop-blur border border-white/20 shadow-lg rounded-full px-4 py-2 text-xs font-semibold text-slate-900 flex items-center gap-1.5 transition"
            >
              <Sparkles size={12} className="text-amber-600" /> Purchase this plan
            </button>
          )}
        </div>
      )}

      {/* Footer info strip */}
      <div className="absolute bottom-0 left-0 right-0 px-4 md:px-6 py-3 bg-gradient-to-t from-black/70 to-transparent text-white/70 text-xs flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Info size={12} className="opacity-70" />
          <span>Scroll to zoom · Drag to pan · Arrows to navigate · R to rotate · Esc to close</span>
        </div>
        <div className="hidden md:block text-white/50 font-mono text-[10px] tracking-wider uppercase">
          © Dream Home Engineers · Preview Only — Not for Construction
        </div>
      </div>

      {/* Thumbnail rail */}
      {images.length > 1 && (
        <div className="absolute bottom-14 left-1/2 -translate-x-1/2 flex gap-2 max-w-[92vw] overflow-x-auto px-2 py-1 z-10">
          {images.map((im, i) => (
            <button
              key={`${im.url}-${i}`}
              data-testid={`lightbox-thumb-${i}`}
              onClick={() => setIndex(i)}
              className={`flex-shrink-0 w-14 h-14 rounded overflow-hidden border-2 transition ${i === index ? "border-white" : "border-white/20 opacity-60 hover:opacity-90"}`}
            >
              <img
                src={im.url}
                alt=""
                loading="lazy"
                decoding="async"
                draggable={false}
                onDragStart={onDragStart}
                onContextMenu={onContextMenu}
                data-protected="true"
                className="w-full h-full object-cover pointer-events-none"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return createPortal(content, document.body);
};

const IconBtn = ({ children, onClick, title, ...rest }) => (
  <button
    onClick={onClick}
    title={title}
    className="rounded-full p-2 bg-white/10 hover:bg-white/25 backdrop-blur text-white transition"
    {...rest}
  >
    {children}
  </button>
);

export default ProtectedLightbox;
